import { HTMLAttributes, ReactNode, SyntheticEvent, useState } from 'react';

import { differenceBy, omit } from 'lodash';

import { createFilterOptions } from 'utils/filterOptions';
import { sortAlphabeticallyBy } from 'utils/misc';

import {
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import MuiAutocompleteOption, {
  checkboxWidth,
  optionPadding
} from './Autocomplete/MuiAutocompleteOption';
import VirtualizedAutocomplete from './Autocomplete/VirtualizedAutocomplete';

type Props<T> = {
  options: T[];
  selectedOptions: T[];
  getOptionLabel?: (option: T) => void;
  renderOption?: (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: T,
    state: AutocompleteRenderOptionState
  ) => ReactNode;
  renderTags?: (options: T[]) => ReactNode;
  idProperty?: string;
  optionLabelProp?: string;
  sortByProp?: string;
  disabled?: boolean;
  extraWidth?: number;
  disablePortal?: true;
  disableSorting?: true;
  disableClearable?: boolean;
  open?: true;
  loading?: boolean;
} & (
  | {
      onChange?: never;
      onSelected: (option: T) => void;
    }
  | {
      onSelected?: never;
      onChange: (options: T[]) => void;
    }
) &
  (
    | {
        renderInput?: never;
        label: string;
      }
    | {
        label?: never;
        renderInput: (params: AutocompleteRenderInputParams) => ReactNode;
      }
  );

export default function MultiSelect<T>(props: Props<T>) {
  const {
    options,
    selectedOptions,
    getOptionLabel: getOptionLabelProp,
    renderOption: renderOptionProp,
    renderTags,
    idProperty = 'id',
    optionLabelProp = 'name',
    sortByProp,
    disabled,
    extraWidth,
    disablePortal,
    disableSorting,
    disableClearable = true,
    open,
    loading,
    ...rest
  } = props;
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setOpen] = useState(open || false);

  const handleChange = (
    e: SyntheticEvent,
    value: T[],
    reason: AutocompleteChangeReason
  ) => {
    if (props.onSelected) {
      if (e.type !== 'click') return;
      let diffOptions: T[];
      if (reason === 'selectOption') {
        diffOptions = differenceBy(value, selectedOptions, idProperty);
      } else {
        diffOptions = differenceBy(selectedOptions, value, idProperty);
      }
      diffOptions.forEach((option) => {
        props.onSelected(option);
      });
    } else {
      props.onChange(value);
    }
  };

  const getOptionLabel = (option) => {
    if (getOptionLabelProp) {
      return getOptionLabelProp(option);
    }
    return option[optionLabelProp] || '';
  };

  const renderOption = (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'> & { key?: string },
    option: T,
    state: AutocompleteRenderOptionState
  ) => {
    if (renderOptionProp) {
      return renderOptionProp(props, option, state);
    }

    props.key = option[idProperty];
    return (
      <MuiAutocompleteOption
        {...props}
        isMulti
        selected={state.selected}
        label={getOptionLabel(option)}
        inputValue={state.inputValue}
      />
    );
  };

  const sortBy = sortByProp || optionLabelProp;

  return (
    <VirtualizedAutocomplete
      {...omit(rest, ['onChange', 'onSelected', 'renderInput', 'label'])}
      autoHighlight
      disableClearable={disableClearable}
      disableCloseOnSelect
      disabled={disabled}
      disableListWrap
      extraWidth={extraWidth || checkboxWidth + optionPadding}
      filterOptions={
        sortByProp ? createFilterOptions(sortBy, [optionLabelProp]) : undefined
      }
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={(a: T, b: T) => a[idProperty] === b[idProperty]}
      limitTags={0}
      multiple
      freeSolo={false}
      onChange={handleChange}
      onInputChange={(event, value, reason) => {
        if (event && event.type === 'blur') {
          setInputValue('');
        } else if (reason === 'selectOption' || reason === 'removeOption') {
          setInputValue(inputValue);
        } else if (reason !== 'reset') {
          setInputValue(value);
        }
      }}
      optionLabelProp={optionLabelProp}
      options={
        disableSorting
          ? options
          : sortAlphabeticallyBy(options as any[], sortBy)
      }
      renderInput={
        props.renderInput
          ? props.renderInput
          : (params) => <TextField {...params} label={props.label} />
      }
      renderOption={renderOption}
      renderTags={renderTags || (() => '')}
      value={selectedOptions}
      disablePortal={disablePortal}
      open={isOpen}
      onOpenCallback={() => setOpen(true)}
      onClose={() => setOpen(false)}
      loading={loading}
    />
  );
}
